/**
 * Barra de pestañas de navegación.
 *
 * Es un `NavLink` por pestaña, no estado local: la URL cambia con la pestaña,
 * así que un enlace a una hoja concreta se comparte, se recarga y responde a
 * atrás/adelante. Un `useState` daría la misma apariencia y perdería las tres
 * cosas.
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

export interface Tab {
  to: string;
  label: string;
  /** Prefijo que se muestra pequeño delante del rótulo: la «03» de una hoja. */
  seq?: string;
  /** Distintivo al final de la pestaña, como el estado de homologación. */
  badge?: ReactNode;
  /** Texto emergente: el nombre exacto de la hoja del Excel que replica. */
  title?: string;
  /** `end` de `NavLink`: solo la pestaña índice necesita coincidencia exacta. */
  end?: boolean;
}

export function Tabs({
  tabs,
  variant = "section",
  label,
}: {
  tabs: Tab[];
  /** `section` es la barra de hojas del paquete; `inline`, la de dentro de una. */
  variant?: "section" | "inline";
  label: string;
}) {
  const bar = useRef<HTMLDivElement>(null);

  // La barra desborda a lo ancho, y entrar por enlace directo a «Técnica
  // Contable» no debe mostrar una barra que parece empezar en Portada. Se
  // desplaza la activa a la vista sin mover la página: `scrollIntoView` sobre
  // el elemento arrastraría también el scroll vertical del documento.
  useEffect(() => {
    const barra = bar.current;
    const activa = barra?.querySelector<HTMLElement>(".tab.active");
    if (!barra || !activa) return;
    const centro = activa.offsetLeft - (barra.clientWidth - activa.offsetWidth) / 2;
    barra.scrollLeft = Math.max(0, centro);
  }, [tabs]);

  if (tabs.length === 0) return null;

  return (
    <nav className={`tabs tabs-${variant}`} aria-label={label} ref={bar}>
      {tabs.map((tab) => (
        <NavLink className="tab" to={tab.to} key={tab.to} end={tab.end} title={tab.title}>
          {tab.seq && <span className="tab-seq">{tab.seq}</span>}
          <span className="tab-label">{tab.label}</span>
          {tab.badge}
        </NavLink>
      ))}
    </nav>
  );
}
